import {
  IMPORT_FILE_EXTENSIONS,
  MAX_IMPORT_TOTAL_BYTES,
  MAX_JSON_BYTES,
} from './constants';

export type ValidateImportFileResult = { ok: true } | { ok: false; reason: string };

const megabytes = (bytes: number): number => Math.round(bytes / (1024 * 1024));

export const isJsonFile = (fileName: string): boolean => fileName.toLowerCase().endsWith('.json');

/**
 * The file-level gate. [AVP-891]
 *
 * JSON gets a much lower cap than CSV on purpose: it is converted to CSV in
 * the browser before upload, so the whole document and its object graph have
 * to fit in memory at once. A delimited file is only ever read a prefix at a
 * time and then streamed to the server in parts.
 */
export const validateImportFile = (file: File): ValidateImportFileResult => {
  const name = file.name.toLowerCase();
  const allowed = IMPORT_FILE_EXTENSIONS.some((extension) => name.endsWith(extension));

  if (!allowed) {
    return { ok: false, reason: `Unsupported file type. Use ${IMPORT_FILE_EXTENSIONS.join(', ')}.` };
  }
  if (file.size === 0) {
    return { ok: false, reason: 'The selected file is empty.' };
  }

  if (isJsonFile(name)) {
    if (file.size > MAX_JSON_BYTES) {
      return {
        ok: false,
        reason: `JSON files are limited to ${megabytes(MAX_JSON_BYTES)} MB because they are `
          + 'converted in the browser. Save this file as CSV and import that instead.',
      };
    }
    return { ok: true };
  }

  if (file.size > MAX_IMPORT_TOTAL_BYTES) {
    return {
      ok: false,
      reason: `File is too large (max ${megabytes(MAX_IMPORT_TOTAL_BYTES)} MB). `
        + 'Split it and import each part.',
    };
  }

  return { ok: true };
};

export default validateImportFile;
